// AGC: Financial Treatment Policy — single source of truth for stock/asset/expense classification.
// AGC: Replaces scattered hardcoded category lists in VoiceRecordingModal, syncService, entryCreator.
// AGC: Frontend calls this to compute badge + toggle; sends the result to backend in `financialTreatment`.

// AGC: How the user's custom category should behave for stock/asset tracking
export type StockTreatmentMode = 'stock' | 'asset' | 'expense' | 'ask'

// AGC: The concrete decision for a single transaction
export type FinancialTreatment = 'stock_in' | 'stock_out' | 'asset_create' | 'asset_dispose' | 'none'

export interface TreatmentInput {
  entryType: 'expense' | 'revenue'
  category: string
  customStockTreatment?: StockTreatmentMode | null
  itemQuantity?: number | null
  itemUnit?: string | null
  title?: string | null
  userOverride?: { trackInStock: boolean } | null
}

export interface TreatmentResult {
  treatment: FinancialTreatment
  displayLabelKey: string
  showStockToggle: boolean
  defaultStockToggleValue: boolean
}

// AGC: Liquid units that suggest tank/bulk storage context
const LIQUID_UNITS = new Set(['l', 'L', 'litro', 'litros', 'gal', 'galao', 'galoes', 'galão', 'galões'])

// AGC: Title keywords that suggest the fuel is being stored (tank), not consumed immediately (gas station)
const FUEL_STORAGE_KEYWORDS = ['diesel', 'tanque', 'estoque', 'armazenar', 'reservatorio', 'reservatório']

/**
 * AGC: Determines if a fuel purchase likely goes into farm storage (tank)
 * AGC: vs immediate consumption (gas station fill-up).
 * AGC: Returns true when we detect liquid quantity + storage-intent keywords.
 */
function hasStrongFuelStockContext(
  title: string | null | undefined,
  itemQuantity: number | null | undefined,
  itemUnit: string | null | undefined,
): boolean {
  const hasVolume = Number(itemQuantity || 0) > 0 && LIQUID_UNITS.has(itemUnit || '')
  const text = String(title || '').toLowerCase()
  const likelyStorage = FUEL_STORAGE_KEYWORDS.some(kw => text.includes(kw))
  return hasVolume && likelyStorage
}

/**
 * AGC: Resolves a custom category's stockTreatment mode into a concrete TreatmentResult.
 */
function resolveCustomMode(
  entryType: 'expense' | 'revenue',
  mode: StockTreatmentMode,
  userOverride?: { trackInStock: boolean } | null,
): TreatmentResult {
  if (mode === 'stock') {
    return {
      treatment: entryType === 'expense' ? 'stock_in' : 'stock_out',
      displayLabelKey: entryType === 'expense' ? 'voice.addsToStock' : 'voice.removesFromStock',
      showStockToggle: false,
      defaultStockToggleValue: false,
    }
  }

  if (mode === 'asset') {
    return {
      treatment: entryType === 'expense' ? 'asset_create' : 'asset_dispose',
      displayLabelKey: entryType === 'expense' ? 'voice.createsAsset' : 'voice.removesAsset',
      showStockToggle: false,
      defaultStockToggleValue: false,
    }
  }

  if (mode === 'ask') {
    // AGC: For revenue + ask, default to no stock movement (simplified for now)
    if (entryType === 'revenue') {
      return {
        treatment: 'none',
        displayLabelKey: 'voice.revenueOnly',
        showStockToggle: false,
        defaultStockToggleValue: false,
      }
    }
    const defaultOn = false
    const chosen = userOverride?.trackInStock ?? defaultOn
    return {
      treatment: chosen ? 'stock_in' : 'none',
      displayLabelKey: chosen ? 'voice.addsToStock' : 'voice.expenseOnly',
      showStockToggle: true,
      defaultStockToggleValue: defaultOn,
    }
  }

  // AGC: mode === 'expense' or fallback
  return {
    treatment: 'none',
    displayLabelKey: entryType === 'expense' ? 'voice.expenseOnly' : 'voice.revenueOnly',
    showStockToggle: false,
    defaultStockToggleValue: false,
  }
}

/**
 * AGC: Main policy function — classifies a financial transaction into its treatment.
 * AGC: Called by VoiceRecordingModal to compute badge + toggle state.
 * AGC: The resulting `treatment` is sent to backend in the confirm payload.
 */
export function getFinancialTreatment(input: TreatmentInput): TreatmentResult {
  const { entryType, category, customStockTreatment, title, itemQuantity, itemUnit, userOverride } = input

  // AGC: Custom categories — resolve via stored stockTreatment mode
  if (customStockTreatment) {
    return resolveCustomMode(entryType, customStockTreatment, userOverride)
  }

  // AGC: Built-in expense categories
  if (entryType === 'expense') {
    switch (category) {
      case 'inputs':
      case 'fertilizer':
      case 'pesticide':
      case 'seeds':
      case 'equipment':
        return {
          treatment: 'stock_in',
          displayLabelKey: 'voice.addsToStock',
          showStockToggle: false,
          defaultStockToggleValue: false,
        }

      case 'machinery_purchase':
      case 'land_purchase':
        return {
          treatment: 'asset_create',
          displayLabelKey: 'voice.createsAsset',
          showStockToggle: false,
          defaultStockToggleValue: false,
        }

      case 'fuel': {
        const defaultOn = hasStrongFuelStockContext(title, itemQuantity, itemUnit)
        const chosen = userOverride?.trackInStock ?? defaultOn
        return {
          treatment: chosen ? 'stock_in' : 'none',
          displayLabelKey: chosen ? 'voice.addsToStock' : 'voice.expenseOnly',
          showStockToggle: true,
          defaultStockToggleValue: defaultOn,
        }
      }

      case 'machinery_parts': {
        const defaultOn = true
        const chosen = userOverride?.trackInStock ?? defaultOn
        return {
          treatment: chosen ? 'stock_in' : 'none',
          displayLabelKey: chosen ? 'voice.addsToStock' : 'voice.expenseOnly',
          showStockToggle: true,
          defaultStockToggleValue: defaultOn,
        }
      }

      // AGC: Period costs — never affect inventory
      case 'maintenance':
      case 'labor':
      case 'transport':
      case 'rent':
      case 'service':
      case 'other':
      default:
        return {
          treatment: 'none',
          displayLabelKey: 'voice.expenseOnly',
          showStockToggle: false,
          defaultStockToggleValue: false,
        }
    }
  }

  // AGC: Built-in revenue categories
  if (entryType === 'revenue') {
    switch (category) {
      // AGC: Sales that deduct from stock
      case 'grain_sale':
      case 'livestock_sale':
      case 'agrochemical_sale':
      case 'stock_sale':
      case 'product_sale':
      case 'animal_sale':
      case 'tool_sale':
        return {
          treatment: 'stock_out',
          displayLabelKey: 'voice.removesFromStock',
          showStockToggle: false,
          defaultStockToggleValue: false,
        }

      // AGC: Asset disposals — machine/land/property sold
      case 'machinery_sale':
      case 'land_sale':
      case 'asset_sale':
        return {
          treatment: 'asset_dispose',
          displayLabelKey: 'voice.removesAsset',
          showStockToggle: false,
          defaultStockToggleValue: false,
        }

      // AGC: Pure revenue — no physical movement
      case 'service':
      case 'rental':
      case 'subsidy':
      case 'other':
      default:
        return {
          treatment: 'none',
          displayLabelKey: 'voice.revenueOnly',
          showStockToggle: false,
          defaultStockToggleValue: false,
        }
    }
  }

  // AGC: Fallback for unexpected entryType
  return {
    treatment: 'none',
    displayLabelKey: 'voice.expenseOnly',
    showStockToggle: false,
    defaultStockToggleValue: false,
  }
}
